import { useEffect, useMemo, useState } from 'react';
import { CollapsibleDocumentPreview, ExpandCollapseButton, useDocumentBlob } from 'domain/shared/documentStorage';
import { ShowSection } from 'framework/ui/layout';
import SignComplianceDocument from 'domain/people/employee/compliance/shared/buttons/SignComplianceDocument';
import { Box, Typography } from '@mui/material';
import SupportRequestWithComplianceDocument from 'domain/people/employee/compliance/shared/buttons/SupportRequestWithComplianceDocument';
import { useShowContext } from 'framework/show';
import { DIFF_TAGS, DiffItem, DiffViewer, MAX_PERCENT_CHANGES } from 'framework/DiffViewer';

const ComplianceDocumentSignContent = () => {
  const { data: contextData } = useShowContext();
  const { documentPath, difference = [], metadata } = contextData.getData() || {};
  const { version, isSignatureRequired } = metadata || {};

  const isFirstSign = useMemo(() => version === 1, [version]);

  const percentChanges = useMemo(() => {
    if (!difference.length) return 0;
    const countDiffs = difference.filter((item: DiffItem) => item.tag !== DIFF_TAGS.EQUAL).length;
    return (countDiffs / difference.length) * 100;
  }, [difference]);

  const { blobUrl, loading, hasError } = useDocumentBlob(documentPath);

  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(prev => !prev);
  };

  useEffect(() => {
    if (isFirstSign || percentChanges === 0 || percentChanges > MAX_PERCENT_CHANGES) {
      setIsExpanded(true);
    }
  }, [isFirstSign, percentChanges]);

  return (
    <ShowSection sx={{ m: '0 !important', paddingBottom: '15px !important' }}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box width="80%">
          <CollapsibleDocumentPreview
            documentPath={documentPath!}
            blobUrl={blobUrl!}
            loading={loading}
            hasError={hasError}
            isExpanded={isExpanded}
          />

          {isFirstSign || percentChanges === 0 || percentChanges > MAX_PERCENT_CHANGES ? null : (
            <>
              <ExpandCollapseButton isExpanded={isExpanded} onToggle={handleToggle} />

              <DiffViewer difference={difference} />
            </>
          )}

          {isSignatureRequired && (
            <Box display="flex" justifyContent="space-between">
              <Box>
                <Typography>
                  In case you are not ready to sign the document and/or need clarifications on its content,
                </Typography>
                <SupportRequestWithComplianceDocument />
              </Box>
              <SignComplianceDocument />
            </Box>
          )}
        </Box>
      </Box>
    </ShowSection>
  );
};

export default ComplianceDocumentSignContent;
